import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RefreshCw, Eye, EyeOff, Trash2, Plus, ExternalLink, Newspaper } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HelpSection = ({ children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-6">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm text-[#3a87fe] hover:text-[#2968d9] font-medium transition">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {open ? 'Hide Instructions' : 'How do I use this?'}
      </button>
      {open && (
        <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-5 text-sm text-gray-700 leading-relaxed space-y-3">
          {children}
        </div>
      )}
    </div>
  );
};

const BlogManager = ({ authHeaders }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/blog/posts`, { headers: authHeaders });
      setPosts(response.data.posts || []);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.post(`${API_URL}/api/admin/blog/generate`, {}, { headers: authHeaders });
      setMessage({ type: 'success', text: 'Blog generation started! It may take 1-2 minutes. Refresh to see the new post.' });
      setTimeout(() => fetchPosts(), 60000); // Refresh after 1 minute
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to start blog generation.' });
    } finally {
      setGenerating(false);
    }
  };

  const handleTogglePublish = async (slug, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/admin/blog/${slug}/publish`, {}, { headers: authHeaders });
      setPosts(posts.map(p => p.slug === slug ? { ...p, published: !currentStatus } : p));
      setMessage({ type: 'success', text: `Post ${!currentStatus ? 'published' : 'unpublished'} successfully.` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update post status.' });
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this blog post? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/blog/${slug}`, { headers: authHeaders });
      setPosts(posts.filter(p => p.slug !== slug));
      setMessage({ type: 'success', text: 'Post deleted successfully.' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete post.' });
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  return (
    <div data-testid="blog-manager">
      <HelpSection>
        <p><strong>CT Remodel News</strong> is your automated blog system that generates SEO-optimized content to help your website rank higher in search engines.</p>
        <p><strong>How it works:</strong></p>
        <ol className="list-decimal ml-5 space-y-1">
          <li><strong>Auto-Generation:</strong> Click "Generate New Post" to create a fresh, AI-written blog post about home remodeling topics</li>
          <li><strong>SEO Optimized:</strong> Each post targets specific keywords that CT homeowners search for</li>
          <li><strong>Publish/Unpublish:</strong> Toggle visibility of any post with the eye icon</li>
          <li><strong>View Live:</strong> Click the external link icon to see the post on your website</li>
        </ol>
        <p className="text-xs text-gray-500 mt-2">Tip: Generate 1-2 posts per week to keep your content fresh and improve SEO rankings.</p>
      </HelpSection>

      {/* Stats & Actions */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#5c0701]/10 rounded-xl flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-[#5c0701]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">CT Remodel News Blog</h2>
              <p className="text-sm text-gray-500">{posts.length} total posts | {posts.filter(p => p.published).length} published</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchPosts}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="flex items-center gap-2 px-4 py-2 bg-[#5c0701] text-white rounded-lg text-sm hover:bg-[#7a0901] transition disabled:opacity-50"
            >
              {generating ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" /> Generating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" /> Generate New Post
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Posts Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5c0701] mx-auto"></div>
          <p className="mt-3 text-gray-500">Loading posts...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Blog Posts Yet</h3>
          <p className="text-gray-500 mb-6">Click "Generate New Post" to create your first AI-generated blog article.</p>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#5c0701] text-white rounded-lg font-medium hover:bg-[#7a0901] transition"
          >
            <Plus className="w-5 h-5" /> Generate First Post
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Title</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Views</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {posts.map((post, index) => (
                <tr key={post.slug || index} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="max-w-md">
                      <p className="font-medium text-gray-900 truncate">{post.title}</p>
                      <p className="text-xs text-gray-500 truncate">/blog/{post.slug}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{formatDate(post.created_at)}</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-600">{post.views || 0}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      post.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {post.published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <a
                        href={`/blog/${post.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-[#3a87fe] transition"
                        title="View post"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleTogglePublish(post.slug, post.published)}
                        className={`p-2 transition ${post.published ? 'text-green-600 hover:text-green-700' : 'text-gray-400 hover:text-gray-600'}`}
                        title={post.published ? 'Unpublish' : 'Publish'}
                      >
                        {post.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(post.slug)}
                        className="p-2 text-gray-400 hover:text-red-600 transition"
                        title="Delete post"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BlogManager;
